import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useOrganization } from '../contexts/OrganizationContext'
import * as orgService from '../services/organization'
import { supabase } from '../services/supabase'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import UpgradeSubscriptionModal from '../components/UpgradeSubscriptionModal'
import StorageUsageDisplay from '../components/StorageUsageDisplay'
import CustomHabitCategoryManager from '../components/CustomHabitCategoryManager'
import { Settings, Users, Crown, Mail, Trash2, Save, UserPlus, DollarSign, FileText, Shield, Sparkles, XCircle, RotateCcw } from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import { formatDate } from '../utils/helpers'
import { useToast } from '../contexts/ToastContext'
import { useMasterAdmin } from '../contexts/MasterAdminContext'
import ConfirmDialog from '../components/ConfirmDialog'
import { cancelSubscription, reactivateSubscription, getSubscriptionStatus } from '../services/subscriptionCancellation'

const OrganizationSettings = () => {
  const { t } = useTranslation()
  const { organization, subscription, reload, isAdmin } = useOrganization()
  const { isMasterAdmin } = useMasterAdmin()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirmDialog, setConfirmDialog] = useState(null)
  const [currentUserId, setCurrentUserId] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    // subscription_tier removed - users can't change it
  })
  const [saving, setSaving] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [availableTiers, setAvailableTiers] = useState([])
  const [selectedTier, setSelectedTier] = useState(null)
  const [upgrading, setUpgrading] = useState(false)
  const [loadingTiers, setLoadingTiers] = useState(false)
  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  const [cancelling, setCancelling] = useState(false)
  const [reactivating, setReactivating] = useState(false)
  const [hasHigherTiers, setHasHigherTiers] = useState(false)

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        // Don't include subscription_tier - users can't edit it
      })
      loadMembers()
      loadSubscriptionStatus()
      checkForHigherTiers()
    }
  }, [organization, subscription])

  useEffect(() => {
    // Get current user ID
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          setCurrentUserId(user.id)
        }
      } catch (error) {
        console.error('Error getting current user:', error)
      }
    }
    getCurrentUser()
  }, [])

  const loadSubscriptionStatus = async () => {
    if (!organization) return
    try {
      const status = await getSubscriptionStatus(organization.id)
      setSubscriptionStatus(status)
    } catch (error) {
      console.error('Error loading subscription status:', error)
    }
  }

  const loadMembers = async () => {
    if (!organization) return
    try {
      setLoading(true)
      const data = await orgService.getOrganizationMembers(organization.id)
      setMembers(data || [])
      
      // Check if current user has a name, if not redirect to profile
      if (currentUserId) {
        const currentUserMember = (data || []).find(m => m.user_id === currentUserId)
        if (currentUserMember && !currentUserMember.user?.name) {
          // User doesn't have a name set, redirect to profile with notice
          success(t('organization.setupNameRequired') || 'Please set up your name in your profile to display properly in team member lists.')
          navigate('/profile', { state: { message: t('organization.setupNameNotice') || 'Please set up your name to display properly in team member lists.' } })
        }
      }
    } catch (error) {
      console.error('Error loading members:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!organization) return
    setSaving(true)
    try {
      // Don't allow users to change subscription tier - only master admin can do that
      const updatesToSave = {
        name: formData.name,
        // subscription_tier is NOT included - users can't change it
      }
      
      await orgService.updateOrganization(organization.id, updatesToSave)
      reload()
      success('Your organization settings have been updated successfully.', {
        title: 'Settings Saved',
      })
    } catch (error) {
      showError(error.message || 'Failed to update organization. Please try again.', {
        title: 'Update Failed',
      })
    } finally {
      setSaving(false)
    }
  }


  const handleUpdateMember = async (memberId, updates) => {
    if (!organization) return
    try {
      await orgService.updateMember(organization.id, memberId, updates, isMasterAdmin)
      loadMembers()
      success('Team member role has been updated successfully.', {
        title: 'Member Updated',
      })
    } catch (error) {
      showError(error.message || 'Failed to update member. Please try again.', {
        title: 'Update Failed',
      })
    }
  }

  const handleRemoveMember = async (memberId) => {
    if (!organization) return
    setConfirmDialog({
      title: 'Remove Team Member',
      message: 'Are you sure you want to remove this team member? They will lose access to your organization.',
      onConfirm: async () => {
        try {
          await orgService.removeMember(organization.id, memberId)
          loadMembers()
          success('Team member has been removed successfully.', {
            title: 'Member Removed',
          })
          setConfirmDialog(null)
        } catch (error) {
          showError(error.message || 'Failed to remove member. Please try again.', {
            title: 'Removal Failed',
          })
          setConfirmDialog(null)
        }
      },
      onClose: () => setConfirmDialog(null),
      confirmText: 'Remove Member',
      variant: 'danger',
    })
  }

  const checkForHigherTiers = async () => {
    if (!organization) return
    try {
      const currentTier = subscription?.tier?.code || subscription?.tier || organization.subscription_tier
      
      // Get all active tiers
      const { data: allTiers, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .eq('is_active', true)
      
      if (error) throw error
      
      // Filter to show only tiers higher than current
      // Tier order: demo < trial < lite < essential < professional < growth < white_label < enterprise
      const tierOrder = ['demo', 'trial', 'lite', 'essential', 'professional', 'growth', 'white_label', 'enterprise']
      const currentTierIndex = tierOrder.indexOf(currentTier)
      
      const higherTiers = (allTiers || []).filter(tier => {
        const tierIndex = tierOrder.indexOf(tier.code)
        return tierIndex > currentTierIndex
      })
      
      setHasHigherTiers(higherTiers.length > 0)
    } catch (error) {
      console.error('Error checking for higher tiers:', error)
      // Default to showing upgrade button if check fails (safer default)
      setHasHigherTiers(true)
    }
  }

  const fetchAvailableTiers = async () => {
    if (!organization) return
    try {
      setLoadingTiers(true)
      const currentTier = subscription?.tier?.code || subscription?.tier || organization.subscription_tier
      
      // Get all active tiers
      const { data: allTiers, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true })
      
      if (error) throw error
      
      // Filter to show only tiers higher than current
      // Tier order: demo < trial < lite < essential < professional < growth < white_label < enterprise
      const tierOrder = ['demo', 'trial', 'lite', 'essential', 'professional', 'growth', 'white_label', 'enterprise']
      const currentTierIndex = tierOrder.indexOf(currentTier)
      
      const higherTiers = (allTiers || []).filter(tier => {
        const tierIndex = tierOrder.indexOf(tier.code)
        return tierIndex > currentTierIndex
      })
      
      setAvailableTiers(higherTiers)
      
      // Auto-select first available tier
      if (higherTiers.length > 0) {
        setSelectedTier(higherTiers[0])
      }
    } catch (error) {
      console.error('Error fetching tiers:', error)
      showError('Failed to load upgrade options. Please try again later.', {
        title: 'Loading Failed',
      })
    } finally {
      setLoadingTiers(false)
    }
  }

  const handleCancelSubscription = () => {
    setConfirmDialog({
      title: 'Cancel Subscription',
      message: subscriptionStatus?.periodEnd
        ? `Your subscription will remain active until ${formatDate(subscriptionStatus.periodEnd)}. After that date, your subscription will be cancelled and you will lose access to premium features. You can reactivate at any time before the end of the billing period.`
        : 'Are you sure you want to cancel your subscription? Your subscription will remain active until the end of the current billing period.',
      confirmText: 'Cancel Subscription',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setCancelling(true)
          await cancelSubscription(organization.id)
          success('Subscription will be cancelled at the end of the billing period')
          await loadSubscriptionStatus()
          reload()
        } catch (error) {
          showError(error.message || 'Failed to cancel subscription')
        } finally {
          setCancelling(false)
          setConfirmDialog(null)
        }
      },
      onClose: () => setConfirmDialog(null),
    })
  }

  const handleReactivateSubscription = async () => {
    try {
      setReactivating(true)
      await reactivateSubscription(organization.id)
      success('Subscription reactivated successfully')
      await loadSubscriptionStatus()
      reload()
    } catch (error) {
      showError(error.message || 'Failed to reactivate subscription')
    } finally {
      setReactivating(false)
    }
  }

  const handleUpgrade = async (billingCycle = 'monthly') => {
    if (!selectedTier || !organization) return
    
    try {
      setUpgrading(true)
      const { upgradeSubscription } = await import('../services/subscriptionUpgrade')
      await upgradeSubscription(organization.id, selectedTier.id, billingCycle)
      // Payment will redirect to Paystack
      success('Redirecting to secure payment page...', {
        title: 'Processing Upgrade',
        duration: 3000,
      })
    } catch (error) {
      showError(error.message || 'Failed to initiate upgrade. Please try again.', {
        title: 'Upgrade Failed',
      })
    } finally {
      setUpgrading(false)
    }
  }

  if (!organization) {
    return (
      <div className="section-container">
        <p className="text-gray-500">{t('organization.loading')}</p>
      </div>
    )
  }

  const tierColors = {
    demo: 'from-gray-500 to-gray-600',
    lite: 'from-teal-500 to-teal-600',
    essential: 'from-blue-500 to-blue-600',
    professional: 'from-green-500 to-green-600',
    growth: 'from-purple-500 to-purple-600', // Code is 'growth', display name is 'Enterprise'
    white_label: 'from-indigo-500 to-indigo-600',
    enterprise: 'from-amber-500 to-amber-600',
  }

  const tierLabels = {
    demo: 'Demo',
    lite: 'Lite',
    essential: 'Essential',
    professional: 'Professional',
    growth: 'Enterprise', // Display name changed from 'Growth' to 'Enterprise'
    white_label: 'White Label',
    enterprise: 'Enterprise',
  }

  return (
    <div className="section-container space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3 mb-8"
      >
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
          <Settings size={24} className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Organization Settings</h1>
          <p className="text-gray-600">Manage your organization, members, and subscription</p>
        </div>
      </motion.div>

      {/* Storage Usage */}
      {isAdmin && <StorageUsageDisplay />}

      {/* Subscription Info */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{t('organization.subscription')}</h2>
            <p className="text-sm text-gray-600">
              {t('organization.currentPlan', { tier: subscription?.tier || organization.subscription_tier })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/billing')}
                className="flex items-center gap-2"
              >
                <DollarSign size={18} />
                {t('organization.billingInvoices')}
              </Button>
              <Link
                to="/legal/terms"
                className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <FileText size={16} />
                {t('organization.terms')}
              </Link>
              <Link
                to="/legal/privacy"
                className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <Shield size={16} />
                {t('organization.privacy')}
              </Link>
            </div>
            <div className={`px-4 py-2 rounded-xl bg-gradient-to-r ${tierColors[subscription?.tier || organization.subscription_tier] || tierColors.demo} text-white font-semibold`}>
              <Crown size={20} className="inline mr-2" />
              {tierLabels[subscription?.tier || organization.subscription_tier] || 'Demo'}
              {subscription?.ends_at && subscription?.tier !== 'demo' && (
                <span className="text-xs ml-2 opacity-90">
                  • {t('organization.ends', { date: formatDate(subscription.ends_at) })}
                </span>
              )}
            </div>
          </div>
        </div>
        {subscription?.tier === 'trial' && subscription?.ends_at && (
          <p className="text-sm text-amber-600">
            Your free trial expires in {Math.ceil((new Date(subscription.ends_at) - new Date()) / (1000 * 60 * 60 * 24))} days
          </p>
        )}
        
        {/* Upgrade Button - Only show for admins and if there are higher tiers available */}
        {isAdmin && hasHigherTiers && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Upgrade Subscription</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Unlock more features and capabilities
                </p>
              </div>
              <Button
                onClick={() => {
                  fetchAvailableTiers()
                  setShowUpgradeModal(true)
                }}
                className="flex items-center gap-2"
              >
                <Sparkles size={18} />
                Upgrade Plan
              </Button>
            </div>
          </div>
        )}

        {/* Subscription Cancellation - Only show for admins, in a less prominent location */}
        {isAdmin && subscription && subscription.tier !== 'demo' && subscription.tier !== 'trial' && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subscription Management</h3>
                {subscriptionStatus?.isCancelled ? (
                  <div className="space-y-2">
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Your subscription is set to cancel at the end of the current billing period ({subscriptionStatus.periodEnd ? formatDate(subscriptionStatus.periodEnd) : 'N/A'}).
                    </p>
                    <button
                      onClick={handleReactivateSubscription}
                      disabled={reactivating}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 disabled:opacity-50"
                    >
                      <RotateCcw size={14} />
                      {reactivating ? 'Reactivating...' : 'Reactivate Subscription'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {subscriptionStatus?.periodEnd 
                        ? `Your subscription renews on ${formatDate(subscriptionStatus.periodEnd)}.`
                        : 'Manage your subscription settings.'}
                    </p>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1 disabled:opacity-50"
                    >
                      <XCircle size={14} />
                      {cancelling ? 'Processing...' : 'Cancel Subscription'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Organization Details */}
      <Card variant="soft" className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{t('organization.organizationDetails')}</h2>
        <div className="space-y-4">
          <Input
            label={t('organization.name')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>{t('common.note')}:</strong> {t('organization.subscriptionNote')}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
            <Save size={18} />
            {saving ? t('organization.saving') : t('organization.saveChanges')}
          </Button>
        </div>
      </Card>

      {/* Custom Habit Categories */}
      {isAdmin && (
        <Card variant="soft" className="p-6">
          <CustomHabitCategoryManager />
        </Card>
      )}

      {/* Team Members */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={24} />
            {t('organization.teamMembers')}
          </h2>
        </div>

        {/* Invite Member - Available to organization admins */}
        {isAdmin && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-100 dark:from-primary-900/20 dark:to-secondary-900/20 dark:border-primary-800">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Invite Team Members</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Send invitation links to add new members. You can copy or share links manually if email sending is not configured.
                </p>
              </div>
              <Button
                onClick={() => navigate('/organization/invite')}
                className="flex items-center gap-2"
              >
                <UserPlus size={18} />
                Manage Invitations
              </Button>
            </div>
          </div>
        )}

        {/* Members List */}
        {loading ? (
          <p className="text-gray-500">Loading members...</p>
        ) : members.length === 0 ? (
          <p className="text-gray-500">No members yet</p>
        ) : (
          <div className="space-y-3">
            {members.map((member) => {
              // Prioritize name over email for display
              const displayName = member.user?.name || member.user?.email || 'Unknown'
              const userEmail = member.user?.email || null
              // Only show email if name exists (as secondary info)
              const displayText = member.user?.name 
                ? `${member.user.name}${userEmail ? ` (${userEmail})` : ''}` 
                : (userEmail || 'Unknown')
              const isCurrentUser = currentUserId && member.user_id === currentUserId
              const canManageMember = isAdmin && !isCurrentUser
              
              return (
                <div
                  key={member.id}
                  className="p-4 rounded-xl bg-white/60 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {displayText}
                      {isCurrentUser && (
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(You)</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{member.role}</p>
                  </div>
                  {canManageMember ? (
                    <div className="flex items-center gap-2">
                      <Select
                        value={member.role}
                        onChange={(e) => handleUpdateMember(member.id, { role: e.target.value })}
                        options={[
                          { value: 'admin', label: 'Admin' },
                          { value: 'practitioner', label: 'Practitioner' },
                          { value: 'viewer', label: 'Viewer' },
                          { value: 'member', label: 'Member' },
                        ]}
                        className="w-40"
                      />
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="p-2 text-red-500 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        title="Remove member"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {isCurrentUser ? (
                        <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                          You cannot change your own role
                        </span>
                      ) : !isAdmin ? (
                        <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                          Admin only
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Upgrade Subscription Modal */}
      {showUpgradeModal && (
        <UpgradeSubscriptionModal
          currentTier={subscription?.tier || organization.subscription_tier}
          availableTiers={availableTiers}
          selectedTier={selectedTier}
          onSelectTier={setSelectedTier}
          onUpgrade={handleUpgrade}
          onClose={() => {
            setShowUpgradeModal(false)
            setSelectedTier(null)
          }}
          upgrading={upgrading}
          loadingTiers={loadingTiers}
        />
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          isOpen={!!confirmDialog}
          onClose={confirmDialog.onClose}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
        />
      )}
    </div>
  )
}

export default OrganizationSettings

